// REACT IMPORTS
import { useMemo } from "react";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// COMPONENT IMPORTS
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import type { PostLoginRoute } from "utils/postLoginRoute";

type ShowPlanDrawer = {
  drawerType: "subscription-method" | "subscription" | "plan";
  id: string;
};

interface InitialState {
  acceptCookies: boolean;
  showWarn: boolean;
  isLogin: boolean;
  isSmallSidebar: boolean;
  showFinalDisclosure: boolean;
  isFinalDisclousreLoading: boolean;
  checkAmount: boolean;
  showOverPaymentOverlay: boolean;
  showPlanDrawer: ShowPlanDrawer | null;
  lastRoute: PostLoginRoute | null;
  lastTab: "payment" | "scheduled";
}

const initialState: InitialState = {
  acceptCookies: true,
  showWarn: true,
  isLogin: false,
  isSmallSidebar: false,
  showFinalDisclosure: false,
  isFinalDisclousreLoading: false,
  checkAmount: false,
  showOverPaymentOverlay: false,
  showPlanDrawer: null,
  lastRoute: null,
  lastTab: "payment",
};

/* ------------------------------------- COMMON SLICE ------------------------------------- */

const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    onAcceptCookies: (state, action) => {
      state.acceptCookies = action.payload;
    },
    onCloseWarn: (state, action) => {
      state.showWarn = action.payload;
    },
    onSetIsLogin: (state, action) => {
      state.isLogin = action.payload;
    },
    onSetSmallSidebar: (state, action) => {
      state.isSmallSidebar = action.payload;
    },
    onSetShowDisclosure: (state, action) => {
      state.showFinalDisclosure = action.payload;
    },
    onSetFinalDisclosureLoading: (state, action) => {
      state.isFinalDisclousreLoading = action.payload;
    },
    updateCheckAmountState: (state, action) => {
      state.checkAmount = action.payload;
    },
    updateShowOverlayState: (state, action) => {
      state.showOverPaymentOverlay = action.payload;
    },
    resetOnRefresh: (state) => {
      state.showFinalDisclosure = false;
      state.isFinalDisclousreLoading = false;
    },
    onSetShowPlanDrawer: (state, action) => {
      state.showPlanDrawer = action.payload;
    },
    onSetLastRoute: (state, action: PayloadAction<PostLoginRoute | null>) => {
      state.lastRoute = action.payload;
    },
    updatePaymentLastTab: (state, action) => {
      state.lastTab = action.payload;
    },
    clearCommonSlice: (state) => {
      for (const key in initialState) {
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof typeof initialState;

          if (
            typedKey === "showPlanDrawer" ||
            typedKey === "lastRoute" ||
            typedKey === "isSmallSidebar"
          ) {
          } else if (typedKey === "lastTab") {
            state[typedKey] = "payment";
          } else {
            state[typedKey] = initialState[typedKey];
          }
        }
      }
    },
  },
});

export const {
  onAcceptCookies,
  onSetIsLogin,
  onCloseWarn,
  onSetSmallSidebar,
  clearCommonSlice,
  onSetShowPlanDrawer,
  onSetShowDisclosure,
  onSetFinalDisclosureLoading,
  updateCheckAmountState,
  updateShowOverlayState,
  resetOnRefresh,
  onSetLastRoute,
  updatePaymentLastTab,
} = commonSlice.actions;
export default commonSlice.reducer;

const selectAcceptCookies = (state: RootState) => state.common.acceptCookies;
const useAcceptCookies = () => {
  const acceptCookies = useAppSelector(selectAcceptCookies);
  return useMemo(() => acceptCookies, [acceptCookies]);
};

const selectIsLogin = (state: RootState) => state.common.isLogin;
export const useIsLogin = () => {
  const isLogin = useAppSelector(selectIsLogin);
  return useMemo(() => isLogin, [isLogin]);
};

const selectShowWarn = (state: RootState) => state.common.showWarn;
export const useShowWarn = () => {
  const showWarn = useAppSelector(selectShowWarn);
  return useMemo(() => showWarn, [showWarn]);
};

const selectIsSmallSidebar = (state: RootState) => state.common.isSmallSidebar;
export const useIsSmallSidebar = () => {
  const isSmallSidebar = useAppSelector(selectIsSmallSidebar);
  return useMemo(() => isSmallSidebar, [isSmallSidebar]);
};

const selectShowFinalDisclosure = (state: RootState) =>
  state.common.showFinalDisclosure;
export const useShowFinalDisclosure = () => {
  const showFinalDisclosure = useAppSelector(selectShowFinalDisclosure);
  return useMemo(() => showFinalDisclosure, [showFinalDisclosure]);
};

const selectIsFinalDisclosureLoading = (state: RootState) =>
  state.common.isFinalDisclousreLoading;
export const useIsFinalDisclosureLoading = () => {
  const isFinalDisclousreLoading = useAppSelector(
    selectIsFinalDisclosureLoading
  );
  return useMemo(() => isFinalDisclousreLoading, [isFinalDisclousreLoading]);
};
